/**
 * Entity Formation Intake Form Option Lists
 * All option lists for dropdowns, radio buttons, and checkboxes.
 * 
 * Format: Each option is { value, label } where:
 * - value: stable snake_case key (stored in form state)
 * - label: display string from the PDF spec
 * 
 * Note: Only the value keys are stored in form state, not the full option objects.
 */

export interface Option {
  value: string;
  label: string;
}

/**
 * Timeline options for business formation
 */
export const TIMELINE_OPTIONS: readonly Option[] = [
  { value: 'immediately', label: 'Immediately' },
  { value: 'within_30_days', label: 'Within 30 days' },
  { value: 'one_to_three_months', label: '1-3 months' },
  { value: 'later', label: 'Later' },
] as const;

/**
 * Revenue expectation ranges
 */
export const REVENUE_EXPECTATIONS_OPTIONS: readonly Option[] = [
  { value: 'under_50k', label: 'Under $50k' },
  { value: '50k_to_250k', label: '$50k - $250k' },
  { value: '250k_to_1m', label: '$250k - $1M' },
  { value: 'over_1m', label: 'Over $1M' },
] as const;

/**
 * Growth outlook options
 */
export const GROWTH_OUTLOOK_OPTIONS: readonly Option[] = [
  { value: 'modest', label: 'Modest' },
  { value: 'significant', label: 'Significant' },
  { value: 'high_growth', label: 'High Growth' },
] as const;

/**
 * Industry/Sector options (multi-select checkbox)
 */
export const INDUSTRY_OPTIONS: readonly Option[] = [
  { value: 'consulting', label: 'Consulting' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'technology_software', label: 'Technology / Software' },
  { value: 'finance_investing', label: 'Finance / Investing' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'media_content', label: 'Media / Content' },
  { value: 'non_profit', label: 'Non-Profit' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Strategic intent options (multi-select checkbox)
 */
export const STRATEGIC_INTENT_OPTIONS: readonly Option[] = [
  { value: 'asset_protection', label: 'Asset Protection' },
  { value: 'raise_capital', label: 'Raise Capital' },
  { value: 'hold_assets', label: 'Hold Assets' },
  { value: 'multiple_businesses', label: 'Multiple Businesses' },
  { value: 'generational_value', label: 'Generational Value' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Number of owners options
 */
export const NUMBER_OF_OWNERS_OPTIONS: readonly Option[] = [
  { value: '1', label: '1' },
  { value: '2_3', label: '2-3' },
  { value: '4_plus', label: '4+' },
] as const;

/**
 * Owner type options
 */
export const OWNER_TYPE_OPTIONS: readonly Option[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'entity', label: 'Entity' },
  { value: 'trust', label: 'Trust' },
] as const;

/**
 * Management preference options
 */
export const MANAGEMENT_PREFERENCE_OPTIONS: readonly Option[] = [
  { value: 'owner_managed', label: 'Owner Managed' },
  { value: 'manager_managed', label: 'Manager Managed' },
  { value: 'board_or_officers', label: 'Board or Officers' },
] as const;

/**
 * Yes/No/Not Sure options
 */
export const YES_NO_NOT_SURE_OPTIONS: readonly Option[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_sure', label: 'Not Sure' },
] as const;

/**
 * Yes/No options
 */
export const YES_NO_OPTIONS: readonly Option[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

/**
 * Yes/No/Later options (for website_timeline)
 */
export const YES_NO_LATER_OPTIONS: readonly Option[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'later', label: 'Later' },
] as const;

/**
 * Liability risk options
 */
export const LIABILITY_RISK_OPTIONS: readonly Option[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'unsure', label: 'Unsure' },
] as const;

/**
 * Risk factor options (multi-select checkbox)
 */
export const RISK_FACTOR_OPTIONS: readonly Option[] = [
  { value: 'handles_client_funds', label: 'Handles Client Funds' },
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'medical_data', label: 'Medical Data' },
  { value: 'construction', label: 'Construction' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'regulated_industry', label: 'Regulated Industry' },
] as const;

/**
 * Initial funding options (multi-select checkbox)
 */
export const INITIAL_FUNDING_OPTIONS: readonly Option[] = [
  { value: 'personal_funds', label: 'Personal Funds' },
  { value: 'loans', label: 'Loans' },
  { value: 'investors', label: 'Investors' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Additional services options (multi-select checkbox)
 */
export const ADDITIONAL_SERVICES_OPTIONS: readonly Option[] = [
  { value: 'fractional_cfo', label: 'Fractional CFO' },
  { value: 'back_office', label: 'Back Office' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'capital_structuring', label: 'Capital Structuring' },
  { value: 'partnerships', label: 'Partnerships' },
  { value: 'ongoing_advisory', label: 'Ongoing Advisory' },
] as const;

